/**
 * Canonical Talk Down Messages shapes for the UI-first library (C1–C5).
 * These types will back the future admin CRUD API ticket.
 */

export type TalkDownAssignmentMode = "entire_site" | "cameras";

export interface TalkDownAssignment {
  siteId: string | number;
  siteName: string;
  mode: TalkDownAssignmentMode;
  cameraIds: Array<string | number>;
  cameraNames: string[];
}

export interface TalkDownMessage {
  id: string;
  name: string;
  groupId: string | number;
  groupName: string;
  durationSeconds: number;
  recordedBy: string;
  recordedAt: string; // ISO
  playCount: number;
  audioUrl: string | null;
  assignments: TalkDownAssignment[];
}

/** Site/camera tree node for the assign picker (mock or localStorage-derived). */
export interface TalkDownAssignSite {
  siteId: string | number;
  siteName: string;
  cameras: Array<{ cameraId: string | number; cameraName: string }>;
}

export interface CreateTalkDownMessageInput {
  name: string;
  groupId: string | number;
  groupName: string;
  durationSeconds: number;
  recordedBy: string;
  audioUrl: string | null;
  assignments: TalkDownAssignment[];
}

export const formatTalkDownDuration = (seconds: number): string => {
  const whole = Math.max(0, Math.floor(seconds));
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

/** C2 "Assigned to" cell copy. */
export const formatTalkDownAssignedTo = (
  assignments: TalkDownAssignment[]
): string => {
  if (!assignments.length) return "Unassigned";

  const siteCount = assignments.length;
  const allEntire = assignments.every((a) => a.mode === "entire_site");
  const allPartial = assignments.every((a) => a.mode === "cameras");

  const countedCameras = assignments.reduce((sum, a) => {
    if (a.mode === "cameras") return sum + a.cameraIds.length;
    return sum + (a.cameraNames.length || a.cameraIds.length);
  }, 0);

  if (allEntire) {
    if (siteCount === 1) {
      const n = countedCameras;
      return n > 0 ? `1 site · ${n} cameras` : "1 site · all cameras";
    }
    return countedCameras > 0
      ? `${siteCount} sites · ${countedCameras} cameras`
      : `${siteCount} sites · all cameras`;
  }

  if (allPartial && siteCount === 1) {
    const n = assignments[0].cameraIds.length;
    return `${n} camera${n === 1 ? "" : "s"}`;
  }

  return `${siteCount} site${siteCount === 1 ? "" : "s"} · ${countedCameras} cameras`;
};
